import {
  ProductReturnsResult,
  Address,
  Barcodes,
  Brand,
  Cart,
  CartCoupon,
  CustomerByEmail,
  CartCustomerUser,
  CartItem,
  CartTax,
  CashBox,
  Collection,
  CollectionInfo,
  CompletedPayment,
  FailedPayment,
  OnBoarding,
  PaginatedCollection,
  PaginatedProducts,
  PaymentMethod,
  PrinterStatus,
  PrinterTypes,
  Product,
  ProductImage,
  ProductOption,
  ProductVariant,
  Settings,
  TaxCountry,
  TaxRateSettings,
  TaxReduced,
  User,
  ProductReturnItem,
  ProductReturnsValidationResult,
  PaymentMethodSettings,
  PickupsAndReservations,
  PickupsAndReservationsCount,
  Pickup,
  PickupItem,
  PickedUpResponse,
  EndOfDayResponse,
  ReversalResponse,
  RefundResponse,
  AuthorizationResponse,
} from '../models';
import {
  Serializer,
  serializeArray,
  serializeBoolean,
  serializeDateTime,
  serializeFeatureFlag,
  serializeGenericObject,
  serializeIdentical,
  serializeNullable,
  serializeNumber,
  serializeObject,
  serializeObjectKeysToSnakeCase,
  serializeOptional,
  serializeText,
  serializeUnknown,
  serializeWithDefault,
  serializeWithFirstMatching,
} from './serializer';

const serializePaymentMethodSettings = serializeObjectKeysToSnakeCase(serializeObject<PaymentMethodSettings>({
  terminalIp: serializeOptional(serializeText)
}));

const serializePaymentMethod = serializeObject<PaymentMethod>({
  id: serializeNumber,
  title: serializeText,
  gateway: serializeText,
  settings: serializeOptional(serializePaymentMethodSettings)
});

const serializeCollectionInfo = serializeObject<CollectionInfo>({
  id: serializeNumber,
  title: serializeText,
});

const serializeUser = serializeObjectKeysToSnakeCase(serializeObject<User>({
  id: serializeNumber,
  pin: serializeText,
  name: serializeText,
  email: serializeText,
  gravatarUrl: serializeText,
  createdAt: serializeText, // TODO: convert to date here!!!
}));

const serializeOnBoarding = serializeObject<OnBoarding>({
  tasks: serializeArray(serializeObject({
    name: serializeText,
    done: serializeBoolean,
    title: serializeText,
    body: serializeText,
  }))
});

const serializeBrand = serializeObjectKeysToSnakeCase(serializeObject<Brand>({
  logoUrl: serializeText,
  themeColor: serializeOptional(serializeText)
}));

const serializeTaxCountry = serializeObjectKeysToSnakeCase(serializeObject<TaxCountry>({
  id: serializeNumber,
  countryCode: serializeText,
  mainTaxRate: serializeNumber
}));

const serializeTaxReduced = serializeObjectKeysToSnakeCase(serializeObject<TaxReduced>({
  collectionId: serializeNumber,
  collectionTitle: serializeText,
  taxRate: serializeNumber
}));

const serializeTaxRates = serializeObjectKeysToSnakeCase(serializeObject<TaxRateSettings>({
  taxCountry: serializeTaxCountry,
  reduced: serializeArray(serializeTaxReduced)
}));

function serializeShopLogoUrl(value: string | null): string {
  if (value === null) {
    return serializeText('');
  }
  return serializeText(value);
}

export const serializeSettings = serializeObjectKeysToSnakeCase(serializeObject<Settings>({
  features: serializeOptional(serializeArray(serializeFeatureFlag)),
  subdomain: serializeText,
  url: serializeText,
  posUuid: serializeText,
  printerType: (value) => serializeText(value) as PrinterTypes,
  epsonIhubDeviceId: serializeOptional(serializeText),
  paperSize: serializeText,
  paymentMethods: serializeArray(serializePaymentMethod),
  collections: serializeArray(serializeCollectionInfo),
  users: serializeArray(serializeUser),
  onboarding: serializeOnBoarding,
  brand: serializeBrand,
  taxRates: serializeTaxRates,
  shopLogoUrl: serializeShopLogoUrl as Serializer<string>,
  sPaymentPosAppId: serializeOptional(serializeText),
  minAppVersion: serializeOptional(serializeText),
}));

const serializeProductImage = serializeObject<ProductImage>({
  id: serializeNumber,
  height: serializeUnknown,
  width: serializeUnknown,
  url: serializeNullable(serializeText)
});

const serializeProductVariant = serializeObjectKeysToSnakeCase(serializeObject<ProductVariant>({
  id: serializeNumber,
  title: serializeText,
  subtitle: serializeNullable(serializeText),
  description: serializeText,
  vendor: serializeNullable(serializeText),
  category: serializeNullable(serializeText),
  price: serializeNumber,
  compareAtPrice: serializeNullable(serializeNumber),
  taxRate: serializeNumber,
  stock: serializeNullable(serializeNumber),
  considersStock: serializeBoolean,
  available: serializeBoolean,
  code: serializeText,
  option01: serializeNullable(serializeUnknown),
  option02: serializeNullable(serializeUnknown),
  option03: serializeNullable(serializeUnknown),
  images: serializeArray(serializeProductImage),
}));


export const serializeProduct = serializeObjectKeysToSnakeCase(serializeObject<Product>({
  id: serializeNumber,
  optionNames: serializeArray(serializeUnknown),
  variants: serializeArray(serializeProductVariant)
}));

export const serializeProducts = serializeArray<Product>(serializeProduct);

export const serializePaginatedProducts = serializeObject<PaginatedProducts>({
  meta: serializeObject({
    page: serializeNumber,
    total: serializeNumber
  }),
  products: serializeProducts
});

const serializeCouponTax = serializeObject<CartTax>({
  rate: serializeNumber,
  amount: serializeNumber,
  standard: serializeOptional(serializeBoolean),
});

const serializeCartCustomer = serializeObjectKeysToSnakeCase(serializeObject<CustomerByEmail>({
  email: serializeText,
  acceptsNewsletter: serializeWithFirstMatching(serializeBoolean, serializeText),
}));

const serializeProductOption = serializeObjectKeysToSnakeCase(serializeObject<ProductOption>({
  name: serializeWithDefault(serializeText, ''),
  position: serializeNumber,
  selectedValue: serializeText,
  values: serializeArray(serializeText)
}));

const serializeCartItem = serializeObjectKeysToSnakeCase(serializeObject<CartItem>({
  id: serializeNumber,
  title: serializeText,
  price: serializeNumber,
  linePrice: serializeNumber,
  quantity: serializeNumber,
  productId: serializeNumber,
  compareAtPrice: serializeNullable(serializeNumber),
  discountAmount: serializeNumber,
  discountType: serializeIdentical,
  image: serializeNullable(serializeProductImage),
  options: serializeArray(serializeProductOption)
}));

const serializeCartCoupon = serializeObjectKeysToSnakeCase(serializeObject<CartCoupon>({
  code: serializeNullable(serializeText),
  valid: serializeBoolean,
  errors: serializeArray(serializeText),
  value: serializeNumber
}));

const serializeSimpleObject = serializeObjectKeysToSnakeCase(serializeObject<Cart>({
  id: serializeText,
  channel: serializeText,
  totalPrice: serializeNumber,
  subtotalPrice: serializeNumber,
  discount: serializeNumber,
  shippingCost: serializeNumber,
  extraPrice: serializeNumber,
  taxAmount: serializeNumber,
  taxRate: serializeNumber,
  taxes: serializeArray(serializeCouponTax),
  customer: serializeCartCustomer,
  lineItems: serializeArray(serializeCartItem),
  note: serializeNullable(serializeText),
  attrs: serializeGenericObject,
  errors: serializeArray(serializeText),
  coupon: serializeCartCoupon,
  reservationId: serializeNullable(serializeNumber),
  isReservation: serializeNullable(serializeBoolean),
}));

export const serializeCart = serializeObject<{ cart: Cart }>({
  cart: serializeSimpleObject
});

export const serializeCollection = serializeObjectKeysToSnakeCase(serializeObject<Collection>({
  id: serializeNumber,
  title: serializeText,
  products: serializeProducts
}));

export const serializePaginatedCollection = serializeObject<PaginatedCollection>({
  id: serializeNumber,
  title: serializeText,
  products: serializeProducts,
  meta: serializeObject({
    total: serializeNumber,
    page: serializeNumber
  })
});

export const serializeCashBox = serializeObjectKeysToSnakeCase(serializeObject<CashBox>({
  cashBalance: serializeNumber,
  expense: serializeNumber,
  reportEposUrl: serializeOptional(serializeText),
  reportImageUrl: serializeOptional(serializeText),
  reportPdfUrl: serializeOptional(serializeText),
  revenue: serializeNumber,
  status: serializeIdentical
}));

export const serializeBarcodes: Serializer<Barcodes> = serializeGenericObject;

const serializeCompletedPayment = serializeObjectKeysToSnakeCase(serializeObject<CompletedPayment>({
  paymentType: serializeText,
  receiptEposUrl: serializeText,
  receiptImageUrl: serializeText,
  status: serializeIdentical,
  change: serializeOptional(serializeNumber),
  invoiceImage: serializeText,
  invoiceUrl: serializeText,
  receiptUrl: serializeText,
  total: serializeNumber
}));

const serializeFailedPayment = serializeObjectKeysToSnakeCase(serializeObject<FailedPayment>({
  error: serializeText,
  status: serializeIdentical
}));

export const serializePaymentResult = serializeWithFirstMatching(serializeCompletedPayment, serializeFailedPayment);

export const serializeProductReturnsItem = serializeObjectKeysToSnakeCase(serializeObject<ProductReturnItem>({
  id: serializeNumber,
  quantity: serializeNumber,
}));

export const serializeProductReturnsValidationResult = serializeObjectKeysToSnakeCase(serializeObject<ProductReturnsValidationResult>({
  transactionId: serializeNumber,
  items: serializeArray(serializeProductReturnsItem),
  payment_type: serializeOptional(serializeText),
  card_type: serializeOptional(serializeText),
}));

export const serializeProductReturnsResult = serializeObjectKeysToSnakeCase(serializeObject<ProductReturnsResult>({
  id: serializeText,
  transactionId: serializeNumber,
  paymentType: serializeOptional(serializeText),
  cardType: serializeNullable(serializeText),
  receiptImageUrl: serializeOptional(serializeText),
  receiptEposUrl: serializeOptional(serializeText),
  receiptUrl: serializeOptional(serializeText),
}));

const serializePickupItem = serializeObjectKeysToSnakeCase(serializeObject<PickupItem>({
  productId: serializeNumber,
  productCode: serializeNullable(serializeText),
  productTitle: serializeNullable(serializeText),
  productQuantity: serializeNumber,
  productPrice: serializeNullable(serializeText),
  option01: serializeNullable(serializeText),
  option02: serializeNullable(serializeText),
  option03: serializeNullable(serializeText),
}));

const serializePickup = serializeObjectKeysToSnakeCase(serializeObject<Pickup>({
  id: serializeNumber,
  name: serializeText,
  email: serializeText,
  createdAt: serializeDateTime,
  pickupAt: serializeNullable(serializeDateTime),
  pickedAt: serializeNullable(serializeDateTime),
  expiresAt: serializeNullable(serializeDateTime),
  isReservation: serializeNullable(serializeBoolean),
  items: serializeArray<PickupItem>(serializePickupItem)
}));

export const serializePickedUpResponse = serializeObjectKeysToSnakeCase(serializeObject<PickedUpResponse>({
  id: serializeNumber,
  receiptEposUrl: serializeText,
  receiptImageUrl: serializeText,
  receiptUrl: serializeText
}));

export const serializePickups = serializeArray<Pickup>(serializePickup);

export const serializeReservationsAndPickups = serializeObjectKeysToSnakeCase(serializeObject<PickupsAndReservations>({
  pickups: serializePickups,
  meta: serializeObject({
    total: serializeNumber,
    page: serializeNumber,
  }),
}));

export const serializeReservationsAndPickupsCount = serializeObjectKeysToSnakeCase(serializeObject<PickupsAndReservationsCount>({
  count: serializeNumber,
}));

export const serializePrinterStatus = serializeObjectKeysToSnakeCase(serializeObject<PrinterStatus>({
  id: serializeText,
  status: serializeText,
  printerState: serializeIdentical,
  jobCount: serializeNumber,
}));

const serializeAddress = serializeObjectKeysToSnakeCase(serializeObject<Address>({
  category: serializeText,
  city: serializeText,
  company: serializeText,
  country: serializeText,
  customerId: serializeNumber,
  draftOrderId: serializeNullable(serializeUnknown),
  email: serializeNullable(serializeText),
  extension: serializeNullable(serializeText),
  firstname: serializeNullable(serializeText),
  id: serializeNumber,
  lastname: serializeNullable(serializeText),
  locked: serializeBoolean,
  option01: serializeNullable(serializeText),
  option02: serializeNullable(serializeText),
  option03: serializeNullable(serializeText),
  orderId: serializeNullable(serializeNumber),
  phone: serializeNullable(serializeText),
  state: serializeText,
  street: serializeText,
  streetnumber: serializeNullable(serializeText),
  title: serializeNullable(serializeText),
  zipcode: serializeNullable(serializeText),
}));

export const serializeCartCustomerUser = serializeObjectKeysToSnakeCase(serializeObject<CartCustomerUser>({
  acceptedOptionalCheckoutCheckbox1: serializeBoolean,
  acceptsNewsletter: serializeBoolean,
  active: serializeBoolean,
  birthday: serializeUnknown,
  checkoutAdditionalCheckboxAccepted: serializeBoolean,
  confirmedAt: serializeUnknown,
  createdOn: serializeText,
  email: serializeNullable(serializeText),
  gender: serializeUnknown,
  id: serializeNumber,
  mobilePhone: serializeNullable(serializeText),
  option01: serializeNullable(serializeText),
  option02: serializeNullable(serializeText),
  option03: serializeNullable(serializeText),
  option04: serializeNullable(serializeText),
  propertiesCount: serializeNumber,
  registered: serializeBoolean,
  riskCheckApproval: serializeUnknown,
  shopId: serializeNumber,
  tagList: serializeUnknown,
  updatedOn: serializeText,
  vatIdentificationNumber: serializeUnknown,
  firstname: serializeNullable(serializeText),
  lastname: serializeNullable(serializeText),
  tags: serializeUnknown,
  billingAddress: serializeOptional(serializeAddress),
  shippingAddress: serializeOptional(serializeAddress),
}));

export const serializeCartCustomerUsers = serializeArray(serializeCartCustomerUser);

export const serializePaymentAuthorization = serializeObjectKeysToSnakeCase(serializeObject<AuthorizationResponse>({
  status: serializeText,
  transactionId: serializeText
}));

export const serializePaymentRefund = serializeObjectKeysToSnakeCase(serializeObject<RefundResponse>({
  status: serializeText,
  transactionId: serializeText
}));

export const serializePaymentReversal = serializeObjectKeysToSnakeCase(serializeObject<ReversalResponse>({
  status: serializeText,
  transactionId: serializeText
}));

export const serializeEndOfDay = serializeObjectKeysToSnakeCase(serializeObject<EndOfDayResponse>({
  status: serializeText,
  transactionId: serializeText
}));
